import { useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import useReadConversationThreadEffect from '../../hooks/useReadConversationThreadEffect';
import ScrollableWrapper from '../../../ScrollableWrapper';
import EntityConversationThreadComment from './EntityConversationThreadComment';
import EntityConversationThreadInput from './EntityConversationThreadInput';
import {
  IConversationThreadFieldsFragment,
  IThreadCommentDeleteInput,
} from '../../../../utils/apollo/graphqlSchema';
import { selectOpenedModalType } from '../../../../redux/selectors/modal';
import { ModalSliceType, openModal } from '../../../../redux/slices/modal';
import DeleteThreadCommentModal from './DeleteThreadCommentModal';
import { ICommentInputInfo } from '../../types';

interface IProps {
  thread: IConversationThreadFieldsFragment;
  onSubmit: (commentInputInfo: ICommentInputInfo) => void;
}

const CommentsWrapper = styled(ScrollableWrapper)`
  max-height: 270px;
`;

const EntityConversationThread = ({ thread, onSubmit }: IProps) => {
  const [editCommentId, setEditCommentId] = useState('');

  const dispatch = useAppDispatch();
  const isCommentingDisabled = useAppSelector(
    (state) => state.conversationThreads.isCommentingDisabled,
  );
  const isDeleteModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.DeleteThreadComment, ''),
  );
  const { id: threadId, entity, comments } = thread;

  const handleDeleteComment = (commentId: string) => {
    dispatch(
      openModal({
        type: ModalSliceType.DeleteThreadComment,
        data: {
          threadId,
          entity: {
            id: entity.id,
            type: entity.type,
          },
          commentId,
        } satisfies IThreadCommentDeleteInput,
      }),
    );
  };
  const handleCancelUpdate = () => {
    setEditCommentId('');
  };

  useReadConversationThreadEffect(thread);

  return (
    <>
      <CommentsWrapper>
        {comments.map((comment, index) => (
          <EntityConversationThreadComment
            key={comment.id}
            thread={thread}
            comment={comment}
            isLast={index === comments.length - 1}
            isFirst={index === 0}
            isEditing={editCommentId === comment.id}
            onEdit={() => setEditCommentId(comment.id)}
            onDelete={() => handleDeleteComment(comment.id)}
            onCancelUpdate={handleCancelUpdate}
          />
        ))}
      </CommentsWrapper>
      {!isCommentingDisabled && (
        <EntityConversationThreadInput thread={thread} onSubmit={onSubmit} />
      )}
      {isDeleteModalOpen && <DeleteThreadCommentModal />}
    </>
  );
};

export default EntityConversationThread;
